import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../../backend/firebaseConfig";

import "../../css/Marketing/FinancialPromotions/FinancialPromotions.css";
import "../../css/StaffHoliday/StaffHoliday.css";
import { useLocation, useNavigate } from "react-router-dom";

import {
  DAY_TYPES,
  LEAVE_STATUS,
  LEAVE_TYPES,
  LEAVE_TYPE_LABELS,
  // ADMIN_LEAVE_TYPE_LABELS,
} from "./utils/staffHolidayConstants";

import { calculateLeaveDays } from "./utils/staffHolidayCalculations";
import { getCachedBankHolidays } from "./services/staffHolidayBankHolidayService";
import {
  submitStaffHolidayRequest,
  updateStaffHolidayRequest,
} from "./services/staffHolidayFirebase";
import { useStaffHolidayProfile } from "./hooks/useStaffHolidayProfile";
import { useStaffHolidayRequests } from "./hooks/useStaffHolidayRequests";
import {
  formatDisplayDate,
  getDatesBetween,
  isWeekend,
  toDateKey,
} from "./utils/staffHolidayDateUtils";
import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import CustomSelect from "../DesignSystem/Dropdowns/CustomSelect";
import DateField from "../DesignSystem/Inputs/DateField";
import TextArea from "../DesignSystem/Inputs/TextArea";
import PrimaryButton from "../DesignSystem/Buttons/PrimaryButton";
import SummaryCards from "../DesignSystem/SummaryCards/SummaryCards";
import Banner from "../DesignSystem/Banner/Banner";
import { useServerSyncedTime } from "../../utils/useServerSyncedTime";
import {
  buildLeaveStatCards,
  getAnnualLeaveStats,
} from "./utils/staffHolidayStaffUtils";

const StaffHolidayRequestForm = () => {
  const { effectiveYear } = useServerSyncedTime();

  const currentYear = Number(effectiveYear) || new Date().getFullYear();
  const currentUserEmail =
    auth.currentUser?.email || localStorage.getItem("userEmail") || "";

  const location = useLocation();
  const navigate = useNavigate();
  // console.log("Profile:", profile);
  const { requests: existingRequests, loading: requestsLoading } =
    useStaffHolidayRequests({
      currentUserEmail,
      mode: "mine",
    });

  const editRequestId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("editRequestId") || "";
  }, [location.search]);

  const editingRequest = useMemo(() => {
    if (!editRequestId) return null;

    return existingRequests.find(
      (request) => (request.requestId || request.id) === editRequestId,
    );
  }, [existingRequests, editRequestId]);

  const isEditing = Boolean(editRequestId);

  const [formData, setFormData] = useState({
    leaveType: LEAVE_TYPES.ANNUAL_LEAVE,
    startDate: "",
    endDate: "",
    startDayType: DAY_TYPES.FULL,
    endDayType: DAY_TYPES.FULL,
    staffNotes: "",
  });

  const [bankHolidays, setBankHolidays] = useState([]);
  const [bannerState, setBannerState] = useState({
    type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [shortNoticeAcknowledged, setShortNoticeAcknowledged] = useState(false);

  useEffect(() => {
    if (!editingRequest) return;

    if (
      !["pending_manager", "pending_holiday_manager", "pending_admin"].includes(
        String(editingRequest.status).toLowerCase(),
      )
    ) {
      setErrorBanner("Only pending holiday requests can be edited.");
      return;
    }

    setFormData({
      leaveType: editingRequest.leaveType || LEAVE_TYPES.ANNUAL_LEAVE,
      startDate: editingRequest.startDate || "",
      endDate: editingRequest.endDate || "",
      startDayType: editingRequest.startDayType || DAY_TYPES.FULL,
      endDayType: editingRequest.endDayType || DAY_TYPES.FULL,
      staffNotes: editingRequest.staffNotes || "",
    });
  }, [editingRequest]);

  const leaveTypeOptions = useMemo(
    () =>
      Object.entries(LEAVE_TYPE_LABELS).map(([value, label]) => ({
        id: value,
        name: label,
      })),
    [],
  );

  const dayTypeOptions = useMemo(
    () => [
      { id: DAY_TYPES.FULL, name: "Full day" },
      { id: DAY_TYPES.MORNING, name: "Morning" },
      { id: DAY_TYPES.AFTERNOON, name: "Afternoon" },
    ],
    [],
  );

  const selectedYear =
    Number(toDateKey(formData.startDate)?.slice(0, 4)) || currentYear;

  const selectedStartDateKey = toDateKey(formData.startDate);
  const selectedEndDateKey = toDateKey(formData.endDate);
  const isEndDateBeforeStartDate = Boolean(
    selectedStartDateKey &&
      selectedEndDateKey &&
      selectedEndDateKey < selectedStartDateKey,
  );

  const { profile, balance } = useStaffHolidayProfile(
    currentUserEmail,
    selectedYear,
  );

  useEffect(() => {
    const loadBankHolidays = async () => {
      try {
        const dates = await getCachedBankHolidays({
          region: profile?.bankHolidayRegion || "england-and-wales",
          year: selectedYear,
        });

        setBankHolidays(dates);
      } catch (error) {
        // console.error("Failed to load bank holidays:", error);
      }
    };

    loadBankHolidays();
  }, [profile?.bankHolidayRegion, selectedYear]);

  const leaveCalculation = useMemo(() => {
    const startDateKey = toDateKey(formData.startDate);
    const endDateKey = toDateKey(formData.endDate);

    if (!startDateKey || !endDateKey) {
      return {
        totalDays: 0,
        includedDates: [],
        excludedDates: [],
      };
    }

    return calculateLeaveDays({
      startDate: startDateKey,
      endDate: endDateKey,
      startDayType: formData.startDayType,
      endDayType: formData.endDayType,
      bankHolidays,
    });
  }, [formData, bankHolidays]);

  const bankHolidayDateKeys = useMemo(() => {
    return new Set(
      bankHolidays.map((holiday) => toDateKey(holiday.date)).filter(Boolean),
    );
  }, [bankHolidays]);

  const isShortNotice = useMemo(() => {
    if (!formData.startDate) return false;

    const startDateKey = toDateKey(formData.startDate);
    if (!startDateKey) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [year, month, day] = startDateKey.split("-");
    const startDate = new Date(Number(year), Number(month) - 1, Number(day));

    const daysUntilHoliday = Math.floor(
      (startDate - today) / (1000 * 60 * 60 * 24),
    );

    return daysUntilHoliday < 14;
  }, [formData.startDate]);

  const annualLeaveStats = useMemo(() => {
    return getAnnualLeaveStats({
      staff: {
        ...(profile || {}),
        holidayBalance: balance,
        leaveBalance: balance,
        balance,
      },
      requests: existingRequests,
      selectedYear,
    });
  }, [balance, profile, existingRequests, selectedYear]);

  const annualLeaveAvailableForRequest = useMemo(() => {
    const remaining = Number(annualLeaveStats.remaining) || 0;

    // The existing pending request is already included in `remaining`. Give
    // those days back while editing so an unchanged request can still be saved.
    const editingRequestDays =
      isEditing &&
      editingRequest?.leaveType === LEAVE_TYPES.ANNUAL_LEAVE &&
      Number(editingRequest.totalDays) > 0
        ? Number(editingRequest.totalDays)
        : 0;

    return remaining + editingRequestDays;
  }, [annualLeaveStats.remaining, editingRequest, isEditing]);

  const hasInsufficientAnnualLeave =
    formData.leaveType === LEAVE_TYPES.ANNUAL_LEAVE &&
    leaveCalculation.totalDays > 0 &&
    leaveCalculation.totalDays > annualLeaveAvailableForRequest;

  const insufficientAnnualLeaveMessage = hasInsufficientAnnualLeave
    ? `You do not have enough annual leave remaining. You have ${annualLeaveAvailableForRequest} day(s) available, but this request is for ${leaveCalculation.totalDays} day(s).`
    : "";

  const summaryCards = useMemo(() => {
    const balanceCards = buildLeaveStatCards(annualLeaveStats).filter(
      (card) => card.id !== "pending",
    );

    return [
      ...balanceCards,
      {
        id: "this-request",
        label: "This Request",
        value: leaveCalculation.totalDays,
        description: "Working days selected",
        tone: "teal",
      },
    ];
  }, [annualLeaveStats, leaveCalculation.totalDays]);

  const updateField = (field, value) => {
    if ((field === "startDate" || field === "endDate") && value) {
      const dateKey = toDateKey(value);

      if (!dateKey) {
        setErrorBanner("Please enter a valid date in DD/MM/YYYY format.");
        return;
      }

      if (isWeekend(dateKey)) {
        setErrorBanner(
          "Weekend dates cannot be selected. Please choose a working day.",
        );
        return;
      }

      if (bankHolidayDateKeys.has(dateKey)) {
        setErrorBanner(
          "Bank holidays cannot be selected. Please choose a working day.",
        );
        return;
      }

      if (field === "endDate") {
        const startDateKey = toDateKey(formData.startDate);

        if (startDateKey && dateKey < startDateKey) {
          setErrorBanner("End date cannot be before start date.");
          return;
        }
      }

      setFormData((prev) => {
        const nextFormData = {
          ...prev,
          [field]: dateKey,
        };

        if (field === "startDate") {
          const currentEndDateKey = toDateKey(prev.endDate);

          if (!currentEndDateKey || currentEndDateKey < dateKey) {
            nextFormData.endDate = dateKey;
          }
        }

        return nextFormData;
      });
      setBannerState({ type: "", message: "" });

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setBannerState({ type: "", message: "" });
  };

  const setErrorBanner = (message) => {
    setBannerState({
      type: "error",
      message,
    });
  };

  const getHolidayItemId = (item = {}) => item.requestId || item.id || "";

  const normaliseStatus = (status = "") =>
    String(status || "")
      .trim()
      .toLowerCase();

  const isInactiveHolidayItem = (item = {}) => {
    const status = normaliseStatus(item.status);

    return (
      status === normaliseStatus(LEAVE_STATUS.REJECTED) ||
      status === normaliseStatus(LEAVE_STATUS.CANCELLED)
    );
  };

  // const getSafeDateKey = (value) => {
  //   if (!value) return "";

  //   if (typeof value?.toDate === "function") {
  //     return toDateKey(value.toDate());
  //   }

  //   if (typeof value === "object" && value.seconds) {
  //     return toDateKey(new Date(value.seconds * 1000));
  //   }

  //   return toDateKey(value);
  // };

  const getSafeDateKey = (value) => {
    if (!value) return "";

    const formatDateObjectAsLocalKey = (date) => {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

      return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
    };

    if (typeof value?.toDate === "function") {
      return formatDateObjectAsLocalKey(value.toDate());
    }

    if (typeof value === "object" && value.seconds) {
      return formatDateObjectAsLocalKey(new Date(value.seconds * 1000));
    }

    if (value instanceof Date) {
      return formatDateObjectAsLocalKey(value);
    }

    const raw = String(value).trim();

    const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      return [
        isoMatch[1],
        String(isoMatch[2]).padStart(2, "0"),
        String(isoMatch[3]).padStart(2, "0"),
      ].join("-");
    }

    const ukMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ukMatch) {
      return [
        ukMatch[3],
        String(ukMatch[2]).padStart(2, "0"),
        String(ukMatch[1]).padStart(2, "0"),
      ].join("-");
    }

    return toDateKey(value) || "";
  };
  const getHolidayStartDate = (item = {}) => {
    return (
      item.startDate ||
      item.date ||
      item.holidayDate ||
      item.from ||
      item.start ||
      item.leaveDate ||
      ""
    );
  };

  const getHolidayEndDate = (item = {}) => {
    return (
      item.endDate ||
      item.date ||
      item.holidayDate ||
      item.to ||
      item.end ||
      item.leaveDate ||
      getHolidayStartDate(item)
    );
  };

  const getImportedProfileHolidayItems = (profile = {}) => {
    return [
      ...(Array.isArray(profile?.trackerHistory) ? profile.trackerHistory : []),
      ...(Array.isArray(profile?.trackerEntries) ? profile.trackerEntries : []),
      ...(Array.isArray(profile?.holidayHistory) ? profile.holidayHistory : []),
      ...(Array.isArray(profile?.importedTrackerHistory)
        ? profile.importedTrackerHistory
        : []),
      ...(Array.isArray(profile?.importedTrackerEntries)
        ? profile.importedTrackerEntries
        : []),
    ];
  };

  const activeExistingRequests = useMemo(() => {
    const importedProfileItems = getImportedProfileHolidayItems(profile);

    return [...existingRequests, ...importedProfileItems].filter((request) => {
      const requestId = getHolidayItemId(request);

      if (isEditing && requestId === editRequestId) {
        return false;
      }

      return !isInactiveHolidayItem(request);
    });
  }, [existingRequests, profile, isEditing, editRequestId]);

  const existingBookedDateKeys = useMemo(() => {
    const keys = new Set();

    const addDateKey = (value) => {
      const dateKey = getSafeDateKey(value);

      if (dateKey) {
        keys.add(dateKey);
      }
    };

    activeExistingRequests.forEach((request) => {
      if (
        Array.isArray(request.includedDates) &&
        request.includedDates.length
      ) {
        request.includedDates.forEach((item) => {
          addDateKey(item?.dateKey || item?.date || item?.startDate || item);
        });

        return;
      }

      const startDateKey = getSafeDateKey(getHolidayStartDate(request));
      const endDateKey = getSafeDateKey(getHolidayEndDate(request));

      if (!startDateKey || !endDateKey) {
        return;
      }

      getDatesBetween(startDateKey, endDateKey).forEach((date) => {
        if (!isWeekend(date)) {
          addDateKey(date);
        }
      });
    });

    return keys;
  }, [activeExistingRequests]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setBannerState({
      type: "",
      message: "",
    });

    if (!currentUserEmail) {
      setErrorBanner("You need to be signed in to submit a leave request.");
      return;
    }

    if (requestsLoading) {
      setErrorBanner(
        "Loading your existing leave requests. Please try again in a moment.",
      );
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      setErrorBanner("Please select a start and end date.");
      return;
    }

    // if (formData.endDate < formData.startDate) {
    //   setErrorBanner("End date cannot be before start date.");
    //   return;
    // }

    const startDateKey = toDateKey(formData.startDate);
    const endDateKey = toDateKey(formData.endDate);

    if (!startDateKey || !endDateKey) {
      setErrorBanner("Please enter dates in DD/MM/YYYY format.");
      return;
    }

    if (endDateKey < startDateKey) {
      setErrorBanner("End date cannot be before start date.");
      return;
    }

    if (isWeekend(startDateKey) || isWeekend(endDateKey)) {
      setErrorBanner(
        "Start date and end date must be working days (not weekends).",
      );
      return;
    }

    if (
      bankHolidayDateKeys.has(startDateKey) ||
      bankHolidayDateKeys.has(endDateKey)
    ) {
      setErrorBanner("Start date and end date cannot be bank holidays.");
      return;
    }

    if (leaveCalculation.totalDays <= 0) {
      setErrorBanner(
        "The selected dates do not include any working leave days.",
      );
      return;
    }

    if (hasInsufficientAnnualLeave) {
      setErrorBanner(insufficientAnnualLeaveMessage);
      return;
    }

    const selectedIncludedDateKeys = leaveCalculation.includedDates
      .map((item) => getSafeDateKey(item?.date || item?.dateKey || item))
      .filter(Boolean);

    const overlappingDates = selectedIncludedDateKeys.filter((dateKey) =>
      existingBookedDateKeys.has(dateKey),
    );

    console.log("[holiday duplicate check]", {
      existingRequests,
      existingBookedDateKeys: Array.from(existingBookedDateKeys),
      leaveCalculationIncludedDates: leaveCalculation.includedDates,
      selectedIncludedDateKeys,
      overlappingDates,
    });

    if (overlappingDates.length > 0) {
      setErrorBanner(
        `You already have leave booked on ${formatDisplayDate(
          overlappingDates[0],
        )}. Please choose different dates.`,
      );
      return;
    }

    if (isShortNotice) {
      if (!shortNoticeAcknowledged) {
        setErrorBanner(
          "Please acknowledge the short notice warning to proceed.",
        );
        return;
      }

      if (!formData.staffNotes || !formData.staffNotes.trim()) {
        setErrorBanner(
          "Please provide notes explaining the reason for short notice booking.",
        );
        return;
      }
    }

    setSubmitting(true);

    try {
      if (isEditing) {
        await updateStaffHolidayRequest({
          staffEmail: currentUserEmail,
          requestId: editRequestId,
          leaveType: formData.leaveType,
          startDate: startDateKey,
          endDate: endDateKey,
          startDayType: formData.startDayType,
          endDayType: formData.endDayType,
          staffNotes: formData.staffNotes,
          updatedBy: currentUserEmail,
        });

        setBannerState({
          type: "success",
          message: "Holiday request updated successfully.",
        });

        navigate("/staff-holiday");
        return;
      }

      await submitStaffHolidayRequest({
        staffEmail: currentUserEmail,
        leaveType: formData.leaveType,
        startDate: startDateKey,
        endDate: endDateKey,
        startDayType: formData.startDayType,
        endDayType: formData.endDayType,
        staffNotes: formData.staffNotes,
        createdBy: currentUserEmail,
      });

      setBannerState({
        type: "success",
        message: "Holiday request submitted successfully.",
      });

      setFormData({
        leaveType: LEAVE_TYPES.ANNUAL_LEAVE,
        startDate: "",
        endDate: "",
        startDayType: DAY_TYPES.FULL,
        endDayType: DAY_TYPES.FULL,
        staffNotes: "",
      });
      setShortNoticeAcknowledged(false);
    } catch (error) {
      // console.error("Failed to submit holiday request:", error);
      setErrorBanner(error.message || "Failed to submit holiday request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="Main-Page">
      <Sidebar />

      <div className="Main-Container">
        <main className="staff-holiday-page">
          <Breadcrumb
            items={[
              { label: "All Staff", to: "/Department/AllStaff" },
              { label: "My Holiday", to: "/staff-holiday" },
              { label: "Request Leave" },
            ]}
          />

          <div className="staff-holiday-header">
            <div>
              {/* <p className="staff-holiday-eyebrow FP_Help_Text_Semibold uppercase">
                Staff Holiday
              </p> */}
              <h1 className="staff-holiday-title FP_H3">Request Leave</h1>
              {/* <p className="staff-holiday-eyebrow FP_Help_Text_Semibold uppercase">
                Submit annual leave, sick leave, unpaid leave or paid leave
                requests.
              </p> */}
            </div>
          </div>

          <SummaryCards cards={summaryCards} grid={4} />

          <div className="staff-holiday-grid">
            <form
              className="staff-holiday-card staff-holiday-form"
              onSubmit={handleSubmit}
            >
              <div className="staff-holiday-form-field">
                <div className="ds-input-label">Leave type</div>
                <CustomSelect
                  value={formData.leaveType}
                  options={leaveTypeOptions}
                  onChange={(event) =>
                    updateField("leaveType", event.target.value)
                  }
                  placeholder="Select leave type"
                  clearable={false}
                  searchable={false}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="staff-holiday-form-row">
                <DateField
                  label="Start date"
                  value={formData.startDate}
                  onChange={(event) =>
                    updateField("startDate", event.target.value)
                  }
                />

                {/* <div className="staff-holiday-form-field">
                  <div className="ds-input-label">Start day</div>
                  <CustomSelect
                    value={formData.startDayType}
                    options={dayTypeOptions}
                    onChange={(event) =>
                      updateField("startDayType", event.target.value)
                    }
                    placeholder="Select day part"
                    clearable={false}
                    searchable={false}
                    style={{ width: "100%" }}
                  />
                </div> */}
                <DateField
                  label="End date"
                  value={formData.endDate}
                  onChange={(event) =>
                    updateField("endDate", event.target.value)
                  }
                />
              </div>

              {/* <div className="staff-holiday-form-row">

                <div className="staff-holiday-form-field">
                  <div className="ds-input-label">End day</div>
                  <CustomSelect
                    value={formData.endDayType}
                    options={dayTypeOptions}
                    onChange={(event) =>
                      updateField("endDayType", event.target.value)
                    }
                    placeholder="Select day part"
                    clearable={false}
                    searchable={false}
                    style={{ width: "100%" }}
                  />
                </div>
              </div> */}

              {isShortNotice && (
                <div>
                  <Banner
                    type="warning"
                    message="Short Notice: We require at least two weeks notice for holiday requests. If you still want to proceed, please check the box below and provide notes explaining the reason."
                  />
                  <div style={{ marginBottom: "16px", marginTop: "16px" }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={shortNoticeAcknowledged}
                        onChange={(e) =>
                          setShortNoticeAcknowledged(e.target.checked)
                        }
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                      />
                      <span className="FP_Content">
                        I acknowledge the short notice and still want to proceed
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <TextArea
                label={
                  isShortNotice && shortNoticeAcknowledged
                    ? "Notes (Required)"
                    : "Notes"
                }
                value={formData.staffNotes}
                onChange={(event) =>
                  updateField("staffNotes", event.target.value)
                }
                placeholder={
                  isShortNotice && shortNoticeAcknowledged
                    ? "Please explain the reason for this short notice request"
                    : "Optional"
                }
                rows={4}
              />

              {bannerState.message && (
                <Banner
                  type={bannerState.type || "error"}
                  message={bannerState.message}
                />
              )}

              {hasInsufficientAnnualLeave &&
                bannerState.message !== insufficientAnnualLeaveMessage && (
                  <Banner
                    type="error"
                    message={insufficientAnnualLeaveMessage}
                  />
                )}

              <PrimaryButton
                type="submit"
                disabled={
                  submitting ||
                  hasInsufficientAnnualLeave ||
                  isEndDateBeforeStartDate
                }
                label={
                  submitting
                    ? isEditing
                      ? "Saving..."
                      : "Submitting..."
                    : isEditing
                      ? "Save Changes"
                      : "Submit Request"
                }
              />
            </form>

            <aside className="staff-holiday-card staff-holiday-summary">
              <h2 className="FP_H3 staff-holiday-summary-title">
                Request Summary
              </h2>

              <div className="staff-holiday-summary-item">
                <span className="FP_Content">Total working days requested</span>
                <strong className="FP_Content_Semibold">
                  {leaveCalculation.totalDays}
                </strong>
              </div>

              <div className="staff-holiday-summary-item">
                <span className="FP_Content">Annual leave remaining</span>
                <strong className="FP_Content_Semibold">
                  {annualLeaveStats.remaining}
                </strong>
              </div>

              <div className="staff-holiday-summary-item">
                <span className="FP_Content">Department</span>
                <strong className="FP_Content_Semibold">
                  {Array.isArray(profile?.departments) &&
                  profile.departments.length
                    ? profile.departments
                        .map((department) => department.name || department.id)
                        .filter(Boolean)
                        .join(", ")
                    : profile?.departmentName || "-"}
                </strong>
              </div>

              <div className="staff-holiday-summary-item">
                <span className="FP_Content">Holiday managers</span>
                <strong className="FP_Content_Semibold text-wrap">
                  {Array.isArray(profile?.holidayManagers) &&
                  profile.holidayManagers.length
                    ? profile.holidayManagers
                        .map((manager) => manager.name || manager.email)
                        .filter(Boolean)
                        .join(", ")
                    : "-"}
                </strong>
              </div>

              {leaveCalculation.excludedDates.length > 0 && (
                <div className="staff-holiday-excluded-box">
                  <h3 className="FP_Subtitle_Semibold primary">
                    Excluded dates
                  </h3>

                  {leaveCalculation.excludedDates.map((item) => (
                    <div key={item.date} className="staff-holiday-excluded-row">
                      <span className="FP_Content">
                        {formatDisplayDate(item.date)}
                      </span>
                      <strong className="FP_Content_Semibold">
                        {item.title || item.reason}
                      </strong>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StaffHolidayRequestForm;
